import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'

import AddPlace from '../trips/AddPlace.jsx'
import ArchivedPlaces from '../trips/ArchivedPlaces.jsx'
import CompareColumn from '../trips/CompareColumn.jsx'
import TierBadge from '../components/TierBadge.jsx'
import { CATEGORIES } from '../trips/estimate.js'
import { columns as buildColumns, comparable, deltas, namesOnScreen } from '../trips/compare.js'
import { directoryNote, useDirectory } from '../directory.js'
import {
  addPlace,
  chooseDestination,
  loadCompare,
  loadEnrichment,
  moveEntry,
  reopenComparison,
  startTripFrom,
} from '../trips/api.js'
import { openLiveChannel } from '../trips/live.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The Trip Compare screen — P5-3 T9, HANDOFF screen 2.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS SCREEN EXISTS ONLY AT TWO OR MORE, AND "EXISTS" MEANS RENDERS
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF calls it the hardest rule and states it as an absence: *"With zero or
// one destination the concept is completely invisible ... The compare screen
// exists only at ≥2 destinations. Most trips are single-destination and must
// not pay a tax for this feature existing."*
//
// A ROUTE existing is not a tax; RENDERING is. So this component's first act
// after the read lands is to check `comparable()` and, when it is false, return
// `<Navigate>` — which puts nothing on the page at all, and lands the member on
// the plain board that T4 already proves renders no destination concept. Not a
// 404: a bookmarked comparison for a trip that has since chosen a winner is not
// an error, and neither is a URL somebody guessed.
//
// ⚠️ THE TEST FOR THIS IS NOT "the component is absent". T4 already learnt that
// the obvious version is worthless: a bare grouping header reading "Portugal"
// satisfies a word-search for "destination" perfectly and is exactly the tax the
// rule forbids. src/test/trips/compare-disclosure.test.jsx drives the REAL
// <App/> at this route with a SENTINEL-named place and asserts that neither the
// word nor the sentinel reaches the document.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 COUNTED AT TWO INCLUDING ARCHIVED — and that is not a loophole
// ═══════════════════════════════════════════════════════════════════════════
//
// A trip that chose Japan has one live place and one archived one. Its
// comparison is over, and this screen still has a job: the archive rows, and
// the two flows off them. Counting only live places would make "Open it,
// read-only" and "Start next year's trip from this" unreachable the instant
// they became relevant.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 LIVENESS IS THE BOARD'S, UNCHANGED — NO SECOND MECHANISM
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §5.6: *"The live channel **is** a tail of this feed. The fallback **is**
// a poll of this feed ... No second mechanism for any of the three, ever."*
//
// Every mutation this screen makes — creating a place, attaching an entry,
// choosing, reopening — goes through the service's `mutation()` seal and
// therefore appends a `change` row, so it rides down the tail that already
// exists. `src/trips/live.js` is untouched by this slice, exactly as T6 left
// it. A change is treated as *"re-read"* rather than as a patch, for the
// board's reason: a change moves the roll-ups, the coverage, which flat
// candidate was counted and which column is dearer, none of it derivable here
// without reimplementing cost.py.

export default function TripCompare({ section }) {
  const { tripId } = useParams()
  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [adding, setAdding] = useState(false)
  // P5-3 T10. Its own state, its own request, its own failure — §6:
  // *"Decoration may fail; the grid may not."* `null` is the ordinary state for
  // the first moment of every load, and the permanent one when a provider is
  // down. Every render path below works from it.
  const [enrichment, setEnrichment] = useState(null)

  // P6-80. The crew's display names, on their own request, with their own
  // failure — spec §7 item 5: *"kill R2: pages render with placeholder names and
  // everything else intact."* It is the same rule as enrichment one line above,
  // and it is enforced harder: `useDirectory` cannot reject, so there is no way
  // for a caller to accidentally couple it to the read that carries the board.
  const directory = useDirectory()

  useDocumentTitle(state.data?.trip?.title ?? section?.title ?? 'Trips')

  // Every read is stamped and only the newest applied — the board's rule, for
  // the board's reason: a live re-read that started before this member's own
  // write and answered after it would put the pre-write screen back up, and
  // they would watch their own change disappear.
  const generation = useRef(0)

  const refresh = useCallback(
    async ({ background = false } = {}) => {
      const mine = generation.current + 1
      generation.current = mine
      try {
        const data = await loadCompare(tripId)
        if (generation.current !== mine) return true
        setState({ status: 'ready', data, error: null })
        return true
      } catch (error) {
        if (generation.current !== mine) return false
        // A refresh nobody asked for never blanks a working screen (§8: the
        // fallback engages "with no user-visible ceremony").
        if (background) return false
        setState({ status: 'error', data: null, error: error.message })
        return false
      }
    },
    [tripId],
  )

  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  // 🔴 `archived: true`, matching this screen's own read. The compare screen is
  // the surface allowed to know about archived places (see the Function), so
  // its enrichment has to cover them or an archived column's converted figure
  // would be missing for a reason that is not about a provider.
  useEffect(() => {
    let live = true
    loadEnrichment(tripId, { archived: true }).then(
      (answer) => {
        if (live) setEnrichment(answer)
      },
      () => {
        if (live) setEnrichment(null)
      },
    )
    return () => {
      live = false
    }
  }, [tripId])

  useEffect(() => {
    let cancelled = false
    let channel = null
    loadCompare(tripId).then(
      (data) => {
        if (cancelled) return
        generation.current += 1
        setState({ status: 'ready', data, error: null })
        channel = openLiveChannel({
          tripId,
          cursor: data.cursor,
          onChange: () => refreshRef.current({ background: true }),
        })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', data: null, error: error.message })
      },
    )
    return () => {
      cancelled = true
      channel?.close()
    }
  }, [tripId])

  const run = useCallback(
    async (work) => {
      setBusy(true)
      setActionError(null)
      try {
        const result = await work()
        await refresh()
        return result ?? true
      } catch (error) {
        setActionError(error.message)
        return false
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  if (state.status === 'loading') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="compare-loading">
          Loading.
        </p>
      </article>
    )
  }

  if (state.status === 'error') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="compare-error">
          {state.error}
        </p>
        <p>
          <Link to={`/trips/${tripId}`}>Back to the board</Link>
        </p>
      </article>
    )
  }

  // 🔴 THE RULE, AND IT RUNS BEFORE ANYTHING IS RENDERED. Nothing below this
  // line executes for a trip with fewer than two places, ever.
  if (!comparable(state.data.destinations)) {
    return <Navigate to={`/trips/${tripId}`} replace />
  }

  const { trip, you } = state.data
  const view = buildColumns(state.data, CATEGORIES, enrichment?.conversion, directory)
  const { deltas: gaps, reason } = deltas(view.columns)
  // Printed only where people are actually drawn — see `namesOnScreen`. The note
  // is `null` while the directory is still loading, because nothing has failed
  // yet and a page that reported an outage on its first frame would be lying.
  const names = namesOnScreen(view) ? directoryNote(directory) : null
  const organiser = trip.your_role === 'organiser'
  const winner = view.chosen
  const needsExisting = view.columns.length + view.archived.length === 0

  const onAdd = (body) =>
    run(async () => {
      const answer = await addPlace(tripId, body)
      // 🔴 The attach count is read out loud rather than assumed. The Function
      // is bounded and not atomic; a board whose entries did not all move is a
      // board with entries in no column, and the only thing that could ever say
      // so is this sentence.
      if (answer?.attachable > answer?.attached) {
        setNotice(
          `${answer.attached} of ${answer.attachable} things moved across. The rest are still on ` +
            `the board and belong to no place yet — they are listed below.`,
        )
      } else {
        setNotice(null)
      }
      setAdding(false)
      return true
    })

  const onChoose = (column) =>
    run(async () => {
      await chooseDestination(tripId, column.id)
      setNotice(null)
      return true
    })

  const onReopen = () =>
    run(async () => {
      await reopenComparison(tripId)
      setNotice(null)
      return true
    })

  const onCopy = (column, title) =>
    run(async () => {
      const answer = await startTripFrom(tripId, { destination_id: column.id, title })
      const copied = Number(answer?.copied ?? 0)
      const total = Number(answer?.total ?? 0)
      setNotice(
        copied === total
          ? `“${title}” started with ${copied} ${copied === 1 ? 'entry' : 'entries'} carried over.`
          : `“${title}” started with ${copied} of ${total} entries. The rest are still on the ` +
            `archived board — nothing was lost, and you can start another one from it.`,
      )
      return true
    })

  const onAttach = (entry, column) => run(() => moveEntry(entry.id, column.id))

  return (
    <article className="d2d-page d2d-compare">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">
          <TierBadge tier={3} />
        </p>
        <p className="d2d-board__back">
          <Link to={`/trips/${tripId}`}>← {trip.title}</Link>
        </p>
        <h1 className="d2d-titleblock__headline">
          {winner ? `We’re going to ${winner.name}` : 'Where are we going?'}
        </h1>
        <p className="d2d-titleblock__lede">
          {winner
            ? `${trip.headcount} ${trip.headcount === 1 ? 'person' : 'people'} · the board has filtered to ${winner.name}`
            : `${view.columns.length} on the table · ${trip.headcount} ${
                trip.headcount === 1 ? 'person' : 'people'
              } · nobody has booked anything`}
        </p>
        <hr className="d2d-rule" />
      </header>

      {actionError ? (
        <p className="d2d-notice d2d-notice--warn" role="status" data-testid="compare-action-error">
          {actionError}
        </p>
      ) : null}

      {notice ? (
        <p className="d2d-notice" role="status" data-testid="compare-notice">
          {notice}
        </p>
      ) : null}

      {winner ? (
        <section className="d2d-winner" aria-label="Where we are going" data-testid="winner">
          <p className="d2d-winner__eyebrow d2d-micro">Where we’re going</p>
          <h2 className="d2d-winner__name">{winner.name}</h2>
          <ul className="d2d-winner__figures">
            {winner.figures.map((figure) => (
              <li key={figure.currency}>
                <span className="d2d-winner__amount">≈ {figure.each}</span>
                <span className="d2d-micro">{figure.currency}</span>
              </li>
            ))}
            {winner.figures.length === 0 ? <li>no amounts yet</li> : null}
          </ul>
          <ul className="d2d-winner__rules">
            {winner.headcount ? <li data-testid="winner-headcount">{winner.headcount}</li> : null}
            <li data-testid="winner-coverage">{winner.coverage}</li>
            {winner.figures.length > 1 ? (
              <li data-testid="winner-currency">
                {winner.figures.length} currencies, kept apart — nothing is converted yet
              </li>
            ) : null}
            {winner.notes.map((note) => (
              <li key={note.key}>{note.text}</li>
            ))}
          </ul>
          <p className="d2d-winner__board">
            <Link to={`/trips/${tripId}`}>Back to the board</Link>
          </p>
        </section>
      ) : null}

      {/* 🔴 THE COLUMNS ARE NOT THE `else` OF THE WINNER BAND, AND THE
          DIFFERENCE IS A PLACE THAT WOULD OTHERWISE VANISH.

          Choosing is two writes and the service offers no transaction across
          requests (see functions/api/trips/[tripId]/choose.js). The order —
          archive the losers, THEN set the winner — is picked so that stopping
          halfway leaves NO winner, which lands here with one or more columns
          and a reversible archive. That state renders truthfully.

          The mirror state, a winner set while another place is still live, is
          not reachable through this site. But if it ever arrives — a direct API
          call, a future writer — an `else` would show the band and NOTHING
          else, while the board filters to the winner: a place with entries,
          stars and notes on it, visible on no surface at all. Silently hiding
          somebody's research is the one thing this product cannot do, so the
          columns render whenever there is still something on the table and the
          contradiction is stated rather than resolved by hiding half of it. */}
      {winner && view.columns.length > 1 ? (
        <p className="d2d-notice d2d-notice--warn" data-testid="still-on-the-table">
          {view.columns.length - 1}{' '}
          {view.columns.length - 1 === 1 ? 'other place is' : 'other places are'} still on the
          table, so this is not finished. Choosing {winner.name} again archives{' '}
          {view.columns.length - 1 === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      {!winner || view.columns.length > 1 ? (
        <>
          {/* 🔴 HORIZONTAL, AND IT SCROLLS BEFORE IT STACKS. HANDOFF's single
              breakpoint rule: below 900px "comparison columns scroll-snap
              horizontally rather than stacking — stacked columns are not a
              comparison." The CSS does that; this is only the flex row. */}
          <div className="d2d-compare__cols" data-testid="columns">
            {view.columns.map((column) => (
              <CompareColumn
                key={column.id}
                column={column}
                tripId={tripId}
                delta={gaps.get(column.id) ?? null}
                limitMusts={view.columns.length >= 3 ? 3 : null}
                canChoose={organiser}
                onChoose={onChoose}
                busy={busy}
              />
            ))}

            {view.columns.length === 2 && !adding ? (
              <button
                type="button"
                className="d2d-compare__slot"
                data-testid="another-place"
                onClick={() => setAdding(true)}
              >
                <span aria-hidden="true">＋</span>
                <span className="d2d-micro">Another place</span>
                <span className="d2d-compare__slothint">
                  Columns get narrower, must-dos cut to three
                </span>
              </button>
            ) : null}
          </div>

          {reason ? (
            <p className="d2d-compare__reason" data-testid="no-delta">
              {reason}
            </p>
          ) : null}

          {adding ? (
            <AddPlace
              needsExisting={needsExisting}
              onAdd={onAdd}
              busy={busy}
              onCancel={() => setAdding(false)}
            />
          ) : view.columns.length !== 2 ? (
            <p className="d2d-compare__add">
              <button type="button" className="d2d-compare__addbutton" onClick={() => setAdding(true)}>
                Compare another place
              </button>
            </p>
          ) : null}

          <p className="d2d-compare__foot">
            Every number here was typed by one of you. Nothing is a booking and nothing is a quote.
            Choosing one archives the rest — it never deletes them.
          </p>
        </>
      ) : null}

      {/* 🔴 RENDERED WHENEVER SOMETHING IS ATTACHED TO NO PLACE, in both
          phases. HANDOFF forbids an "ungrouped" bucket and this is deliberately
          not one: it is a sentence and a list of names, never a column beside
          the real ones, because a nameless column reads as a place. It exists
          because the alternative is entries that are on the board, counted in
          no column, and mentioned nowhere. */}
      {view.loose.entries.length > 0 ? (
        <section className="d2d-loose" data-testid="loose">
          <p className="d2d-loose__line">
            {view.loose.entries.length}{' '}
            {view.loose.entries.length === 1 ? 'thing is' : 'things are'} on the board and with no
            place yet, so {view.loose.entries.length === 1 ? 'it is' : 'they are'} counted in no
            column above.
          </p>
          <ul className="d2d-loose__list">
            {view.loose.entries.map((entry) => (
              <li key={entry.id} className="d2d-loose__row" data-testid={`loose-${entry.id}`}>
                <span className="d2d-loose__title">{entry.title}</span>
                <span className="d2d-loose__actions">
                  {[...view.columns, ...view.archived].map((column) => (
                    <button
                      key={column.id}
                      type="button"
                      className="d2d-loose__put"
                      disabled={busy}
                      onClick={() => onAttach(entry, column)}
                    >
                      put it with {column.name}
                    </button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {view.archived.length > 0 ? (
        <ArchivedPlaces
          archived={view.archived}
          tripId={tripId}
          winnerName={winner?.name ?? null}
          canReopen={organiser}
          onReopen={onReopen}
          onCopy={onCopy}
          busy={busy}
        />
      ) : null}

      {/* 🔴 WHERE THE NAMES CAME FROM AND WHEN THEY WERE LAST CHECKED — spec §2,
          *"`as_of` is displayed wherever staleness could mislead"*, and a name is
          the fact that goes stale most quietly: somebody changes their nickname
          and every screen keeps saying the old one until the next reconcile,
          with nothing on the page admitting it.

          It is LAST rather than beside the columns because it is provenance for
          the whole screen — the archive draws must-dos too, and the archive is
          exactly where a departed member's name matters most.

          The absent case is the same element with a different string, which is
          `enrichment.js`'s rule verbatim: *"A blank and a label are the same code
          path with a different string, and the string is the whole difference
          between the two."* */}
      {names ? (
        <p className="d2d-compare__names" data-testid="directory-note">
          {names}
        </p>
      ) : null}
    </article>
  )
}
